import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';

/** One open, back-closable UI layer. */
interface BackLayer {
  /** Close this layer (a dialog's ref.close(), or a custom teardown). */
  close: () => void;
}

/**
 * Makes the browser / Android Back button close ONLY the topmost open UI layer
 * (a MatDialog, or a non-dialog overlay registered via `pushLayer`) instead of
 * tearing the whole screen back to the start.
 *
 * Mechanism: we keep **one extra "trap" history entry per open layer**. A Back
 * press pops one trap — a *same-URL* popstate, so with
 * `onSameUrlNavigation: 'ignore'` the router does not navigate — and we close the
 * single topmost layer here. Because each layer owns its own trap, closing a
 * layer never needs a `pushState` from inside the popstate handler (that is
 * unreliable across browsers): the next layer's trap already sits in history
 * below, and the topmost layer's trap was just consumed by this very Back.
 *
 * Material's own `closeOnNavigation` is turned OFF app-wide (via
 * `MAT_DIALOG_DEFAULT_OPTIONS`) so the CDK no longer disposes *every* dialog on
 * that popstate; this service is the only thing that closes layers.
 *
 * Generic and kept in core so any eMeni app can opt in by calling `init()` once
 * from its root component.
 */
@Injectable({
  providedIn: 'root'
})
export class BackNavigationService {
  /** Open layers, oldest first / topmost last. One trap history entry per layer. */
  private layers: BackLayer[] = [];
  /** Count of history.back() calls WE triggered, whose popstate must be ignored. */
  private pendingSelfPops = 0;
  private started = false;

  /** Wire global Back handling. Call once, from the root AppComponent. */
  init(dialog: MatDialog, router: Router): void {
    if (this.started) { return; }
    this.started = true;

    // Track every Material dialog (charge, modifier, buyer, keypad, alerts…).
    // The root MatDialog's afterOpened also sees dialogs opened from lazy modules.
    dialog.afterOpened.subscribe((ref: MatDialogRef<unknown>) => {
      const layer: BackLayer = { close: () => ref.close() };
      this.add(layer);
      ref.afterClosed().subscribe(() => this.remove(layer));
    });

    // A real route change (login/logout, company switch, expired session) tears
    // down any open dialogs — closeOnNavigation is off, so we do it explicitly.
    // Same-URL trap pops have `e.url === router.url`, so they are skipped here.
    router.events.subscribe(e => {
      if (e instanceof NavigationStart && e.url !== router.url && this.layers.length) {
        this.layers = [];
        dialog.closeAll();
        // Leftover trap entries become harmless same-URL history orphans; the
        // navigation itself rewrites history, so we don't try to pop them here.
      }
    });

    window.addEventListener('popstate', this.onPopState);
  }

  /**
   * Register a non-dialog layer (e.g. the embedded order screen). Returns an
   * idempotent remover, safe to call even after Back already closed the layer.
   */
  pushLayer(close: () => void): () => void {
    const layer: BackLayer = { close };
    this.add(layer);
    return () => this.remove(layer);
  }

  private add(layer: BackLayer): void {
    this.layers.push(layer);
    history.pushState(null, ''); // this layer's trap entry (pushed OUTSIDE popstate)
  }

  /** Remove a layer that closed by something OTHER than Back (save, X, completion…). */
  private remove(layer: BackLayer): void {
    const i = this.layers.indexOf(layer);
    if (i === -1) { return; } // already gone (e.g. closed by Back) — idempotent
    this.layers.splice(i, 1);
    // Drop this layer's trap entry. Same-URL, so the router ignores it.
    this.pendingSelfPops++;
    history.back();
  }

  private onPopState = (): void => {
    if (this.pendingSelfPops > 0) { this.pendingSelfPops--; return; } // our own history.back()
    if (!this.layers.length) { return; }                             // nothing ours → real Back

    const top = this.layers.pop()!; // this layer's trap was just consumed by the Back
    top.close();                    // close ONLY the topmost layer (no pushState needed)
  };
}
